import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MapPin, Phone, Clock, CheckCircle, ArrowRight, Star, Calendar, Users, Award } from 'lucide-react';
import { Button } from '../components/ui/button';

// Dati delle località
const LOCATIONS = {
  'milano': {
    slug: 'oculista-milano',
    city: 'Milano',
    status: 'active',
    title: 'Oculista Milano | Centro Oftalmologico PRIME VISION',
    metaDescription: 'Centro oculistico a Milano. Visite specialistiche, chirurgia refrattiva, cataratta, strabismo. Prenota ora la tua visita con PRIME VISION.',
    keywords: 'oculista milano, centro oculistico milano, oftalmologo milano, visita oculistica milano, chirurgia refrattiva milano',
    h1: 'Oculista a Milano',
    address: 'Via Example 123, 20121 Milano',
    phone: '+39 02 1234567',
    heroImage: 'https://images.unsplash.com/photo-1551076805-e1869033e561?w=1200',
    services: [
      { name: 'Visita Oculistica Completa', price: '€90', duration: '45 min', icon: '👁️' },
      { name: 'Chirurgia Refrattiva LASIK', price: 'Da €1.500/occhio', duration: '30 min', icon: '✨' },
      { name: 'Intervento Cataratta', price: 'Da €2.000/occhio', duration: '20 min', icon: '💎' },
      { name: 'Trattamento Strabismo', price: 'Su consulto', duration: 'Variabile', icon: '👀' },
      { name: 'Cross-Linking Cheratocono', price: 'Da €1.200/occhio', duration: '60 min', icon: '🔬' },
      { name: 'Iniezioni Intravitreali', price: 'Da €800', duration: '15 min', icon: '💉' }
    ],
    specializations: [
      'Chirurgia Refrattiva (LASIK, PRK, SMILE)',
      'Chirurgia della Cataratta',
      'Trattamento Strabismo adulti e bambini',
      'Glaucoma e Maculopatia',
      'Oftalmologia Pediatrica',
      'Cheratocono e Cross-Linking'
    ],
    faq: [
      { q: 'Quanto costa una visita oculistica a Milano?', a: 'La visita oculistica completa costa €90 e include tutti gli esami diagnostici di base.' },
      { q: 'La chirurgia refrattiva è dolorosa?', a: "No, l'intervento è indolore grazie all'anestesia locale in collirio. Dura solo 10-15 minuti per occhio." },
      { q: 'Lo strabismo si può operare da adulti?', a: 'Sì, lo strabismo si opera con successo anche in età adulta. I risultati estetici e funzionali sono eccellenti.' },
      { q: 'Accettate assicurazioni sanitarie?', a: 'Sì, siamo convenzionati con le principali assicurazioni e fondi sanitari.' }
    ],
    stats: [
      { value: '15.000+', label: 'Pazienti soddisfatti' },
      { value: '98%', label: 'Tasso di successo' },
      { value: '20+', label: 'Anni di esperienza' },
      { value: '24h', label: 'Appuntamento rapido' }
    ]
  },
  'napoli': {
    slug: 'oculista-napoli',
    city: 'Napoli',
    status: 'coming_soon',
    comingSoonText: 'PROSSIMA APERTURA',
    title: 'Oculista Napoli | Centro Oftalmologico PRIME VISION',
    metaDescription: 'Centro oculistico a Napoli. Visite specialistiche, chirurgia refrattiva, cataratta, strabismo. PROSSIMAMENTE - Prenota ora per essere tra i primi!',
    keywords: 'oculista napoli, centro oculistico napoli, oftalmologo napoli, visita oculistica napoli',
    h1: 'Oculista a Napoli',
    address: 'Apertura prevista: 2026',
    phone: '+39 081 XXXXXXX',
    heroImage: 'https://images.unsplash.com/photo-1516483638261-f4dbaf036963?w=1200',
    services: [
      { name: 'Visita Oculistica Completa', price: '€80', duration: '45 min', icon: '👁️' },
      { name: 'Chirurgia Refrattiva', price: 'Da €1.400/occhio', duration: '30 min', icon: '✨' },
      { name: 'Intervento Cataratta', price: 'Da €1.800/occhio', duration: '20 min', icon: '💎' },
      { name: 'Trattamento Strabismo', price: 'Su consulto', duration: 'Variabile', icon: '👀' }
    ],
    specializations: [
      'Chirurgia Refrattiva (LASIK, PRK)',
      'Chirurgia della Cataratta',
      'Trattamento Strabismo',
      'Glaucoma e Maculopatia',
      'Oftalmologia Pediatrica'
    ],
    faq: [
      { q: 'Quando apre PRIME VISION Napoli?', a: "L'apertura è prevista per il 2026. Iscriviti alla lista d'attesa per essere tra i primi!" },
      { q: 'Posso fare una visita online?', a: 'Sì, offriamo teleconsulti con i nostri specialisti. Prenota dal sito.' }
    ],
    stats: []
  },
  'roma': {
    slug: 'oculista-roma',
    city: 'Roma',
    status: 'coming_soon',
    comingSoonText: 'PROSSIMA APERTURA',
    title: 'Oculista Roma | Centro Oftalmologico PRIME VISION',
    metaDescription: 'Centro oculistico a Roma. Visite specialistiche, chirurgia refrattiva, cataratta. PROSSIMAMENTE - Iscriviti alla lista d\'attesa!',
    keywords: 'oculista roma, centro oculistico roma, oftalmologo roma, visita oculistica roma',
    h1: 'Oculista a Roma',
    address: 'Apertura prevista: 2026',
    phone: '+39 06 XXXXXXX',
    heroImage: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=1200',
    services: [],
    specializations: [],
    faq: [],
    stats: []
  }
};

const LocationPage = () => {
  const { city } = useParams();
  const [email, setEmail] = useState('');
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  
  const cityKey = city?.toLowerCase().replace('oculista-', '');
  const location = LOCATIONS[cityKey];
  
  if (!location) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Pagina non trovata</h1>
          <Link to="/" className="text-teal-600 hover:underline">Torna alla home</Link>
        </div>
      </div>
    );
  }

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seo/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, city: location.city })
      });
      if (response.ok) {
        setWaitlistSubmitted(true);
      }
    } catch (error) {
      console.error('Errore:', error);
    }
  };

  return (
    <>
      <Helmet>
        <title>{location.title}</title>
        <meta name="description" content={location.metaDescription} />
        <meta name="keywords" content={location.keywords} />
        <link rel="canonical" href={`https://primevision.it/${location.slug}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={location.title} />
        <meta property="og:description" content={location.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://primevision.it/${location.slug}`} />
        
        {/* Schema.org LocalBusiness */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalBusiness",
            "name": `PRIME VISION - ${location.h1}`,
            "description": location.metaDescription,
            "url": `https://primevision.it/${location.slug}`,
            "telephone": location.phone,
            "address": {
              "@type": "PostalAddress",
              "streetAddress": location.address,
              "addressLocality": location.city,
              "addressCountry": "IT"
            },
            "medicalSpecialty": "Ophthalmology",
            "priceRange": "€€"
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative h-[60vh] min-h-[500px] overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${location.heroImage})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-teal-900/90 to-teal-800/70" />
          </div>
          
          <div className="relative z-10 container mx-auto px-4 h-full flex flex-col justify-center">
            {location.status === 'coming_soon' && (
              <span className="inline-block bg-amber-500 text-white px-4 py-1 rounded-full text-sm font-semibold mb-4 w-fit">
                {location.comingSoonText}
              </span>
            )}
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              {location.h1}
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mb-8">
              Centro oftalmologico d'eccellenza PRIME VISION. Tecnologie all'avanguardia e specialisti di alto livello.
            </p>
            
            <div className="flex flex-wrap gap-4">
              {location.status === 'active' ? (
                <>
                  <Link to="/prenota">
                    <Button size="lg" className="bg-white text-teal-800 hover:bg-gray-100">
                      <Calendar className="w-5 h-5 mr-2" />
                      Prenota Visita
                    </Button>
                  </Link>
                  <a href={`tel:${location.phone}`}>
                    <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                      <Phone className="w-5 h-5 mr-2" />
                      Chiama Ora
                    </Button>
                  </a>
                </>
              ) : (
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 max-w-md">
                  {!waitlistSubmitted ? (
                    <form onSubmit={handleWaitlistSubmit} className="space-y-4">
                      <h3 className="text-white font-semibold">Iscriviti alla lista d'attesa</h3>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="La tua email"
                        className="w-full px-4 py-2 rounded-lg text-gray-900"
                        required
                      />
                      <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-600">
                        Avvisami all'apertura
                      </Button>
                    </form>
                  ) : (
                    <div className="text-white text-center">
                      <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                      <p>Grazie! Ti contatteremo presto.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Stats */}
        {location.stats?.length > 0 && (
          <section className="bg-teal-50 py-12">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {location.stats.map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-3xl md:text-4xl font-bold text-teal-700">{stat.value}</div>
                    <div className="text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Services */}
        {location.services?.length > 0 && (
          <section className="py-16">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                I Nostri Servizi a {location.city}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {location.services.map((service, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                    <span className="text-3xl mb-4 block">{service.icon}</span>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{service.name}</h3>
                    <div className="flex justify-between text-gray-600">
                      <span>{service.price}</span>
                      <span className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {service.duration}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Specializations */}
        {location.specializations?.length > 0 && (
          <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                Le Nostre Specializzazioni
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
                {location.specializations.map((spec, i) => (
                  <div key={i} className="flex items-center bg-white rounded-lg p-4 shadow-sm">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-3 flex-shrink-0" />
                    <span className="text-gray-800">{spec}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ */}
        {location.faq?.length > 0 && (
          <section className="py-16">
            <div className="container mx-auto px-4 max-w-3xl">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                Domande Frequenti
              </h2>
              <div className="space-y-4">
                {location.faq.map((item, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="font-semibold text-gray-900 mb-2">{item.q}</h3>
                    <p className="text-gray-600">{item.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-16 bg-teal-700">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              {location.status === 'active' 
                ? 'Prenota la Tua Visita Oggi'
                : `Non Vediamo l'Ora di Accoglierti a ${location.city}`
              }
            </h2>
            <p className="text-white/90 mb-8 max-w-2xl mx-auto">
              {location.status === 'active'
                ? 'Il nostro team di specialisti è pronto ad aiutarti. Prenota ora e ricevi risposta entro 24 ore.'
                : 'Iscriviti alla lista d\'attesa per ricevere uno sconto esclusivo sulla prima visita!'
              }
            </p>
            {location.status === 'active' ? (
              <Link to="/prenota">
                <Button size="lg" className="bg-white text-teal-800 hover:bg-gray-100">
                  Prenota Ora <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            ) : (
              <Link to={`/${location.slug}#waitlist`}>
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white">
                  Iscriviti alla Lista d'Attesa
                </Button>
              </Link>
            )}
          </div>
        </section>

        {/* Contact Info */}
        {location.status === 'active' && (
          <section className="py-12 bg-gray-50">
            <div className="container mx-auto px-4">
              <div className="flex flex-wrap justify-center gap-8 text-center">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-teal-600" />
                  <span className="text-gray-700">{location.address}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-teal-600" />
                  <a href={`tel:${location.phone}`} className="text-teal-600 hover:underline">
                    {location.phone}
                  </a>
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default LocationPage;
